import Loading from '@common/components/Loading';
import StatusBar from '@common/components/StatusBar';
import useBindingScreen from '@common/hooks/useBindingScreen';
import useLineAction from '@common/hooks/useLineAction';
import useScreenAction from '@common/hooks/useScreenAction';
import { appInfoSelector, locationSelector } from '@common/redux/selectors/app';
import { profileSelector } from '@common/redux/selectors/auth';
import {
  currentRecordSelector,
  realtimeDatabaseSelector,
} from '@common/redux/selectors/database';
import { getValueFields } from '@common/redux/selectors/formInputs';
import { localeSelector } from '@common/redux/selectors/language';
import {
  dimensionSelector,
  showQrCodeValueSelector,
} from '@common/redux/selectors/page';
import { useLocation } from '@common/routes/hooks';
import { PropRender } from '@common/types/';
import {
  getAttributesConstrain,
  getAttributesFixed,
  getAttributesNormal,
} from '@common/utils';
import {
  eachRecursiveTable,
  flattenInputObject,
  memoriesConditional,
} from '@common/utils/component';
import { get } from 'lodash';
import { default as queryString, ParsedQuery } from 'query-string';
import React, { useMemo } from 'react';
import { Platform, SafeAreaView, View } from 'react-native';
import { useSelector } from 'react-redux';
import { loadingPageSelector } from '../../../redux/selectors/page';
import BackgroundImage from '../RenderScreen/BackgroundImage';
import FixedComponent from './FixedObject';
import NormalComponent from './DynamicObject';
import createStyle from './styles';
import QrCodeScanner from '@common/components/QrCodeScanner';
import ConstrainObject from '@common/screens/RenderScreen/Render/ConstrainObject';
import { STATIC_VALUES } from '@common/constants/index';

function Render({ screen }: PropRender) {
  const appInfo = useSelector(appInfoSelector);
  const dimension = useSelector(dimensionSelector);
  const loadingPage = useSelector(loadingPageSelector);
  const valueInputs = useSelector(getValueFields);
  const currentRecord = useSelector(currentRecordSelector);
  const databases = useSelector(realtimeDatabaseSelector);
  const loggedUser = useSelector(profileSelector);
  const currentLocation = useSelector(locationSelector);
  const locale = useSelector(localeSelector);
  const qrCode = useSelector(showQrCodeValueSelector);
  const { search } = useLocation();

  const { hiddenStatusBar }: ParsedQuery<any> = useMemo(
    () => queryString.parse(search),
    [search]
  );

  const tableRefresh = useMemo(() => {
    const pullToRefresh = get(screen, 'attributes.pullToRefresh', false);
    if (!pullToRefresh) return [];

    const tables = eachRecursiveTable(screen);
    return tables.reduce((preValue: any, current: string) => {
      return { ...preValue, [current]: databases[current] };
    }, {});
  }, [databases, screen]);

  const inputDependencies = useMemo(() => flattenInputObject(screen), [screen]);

  const inputs = useMemo(() => {
    if (inputDependencies.length <= 0) return;
    return inputDependencies.reduce((preValue: any, current: string) => {
      return { ...preValue, [current]: get(valueInputs, current) };
    }, {});
  }, [inputDependencies, valueInputs]);

  const dependencies: any = useMemo(() => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const staticValues = inputDependencies.filter((value: string) =>
      Object.keys(STATIC_VALUES).includes(value)
    );

    return {
      appInfor: appInfo,
      valueInputs: inputs,
      routeParam: currentRecord,
      table: tableRefresh,
      timeZone,
      loggedUser: loggedUser?.id,
      currentLocation,
      locale,
      scannerId: qrCode,
      staticValues, // this value will be updated in makeClientProps
      dimension,
    };
  }, [
    inputs,
    appInfo,
    currentRecord,
    screen?.screenId,
    tableRefresh,
    loggedUser,
    currentLocation,
    qrCode,
    inputDependencies,
    dimension,
  ]);

  useBindingScreen({ screen, dependencies, appInfo });

  useLineAction({ dependencies });

  useScreenAction({ screen, dependencies });

  const isWeb = useMemo(() => appInfo?.platform === 'web', [appInfo]);
  const isResponsive = useMemo(
    () => appInfo?.platform === 'responsive',
    [appInfo]
  );

  const isShowStatusBar =
    !screen.isModal &&
    !(Platform.OS === 'android' || Platform.OS === 'ios') &&
    !hiddenStatusBar &&
    screen?.attributes?.statusBarStyle !== 'hidden';

  const arrNormal = getAttributesNormal(screen);
  const arrFixed = getAttributesFixed(screen);
  const arrConstrain = getAttributesConstrain(screen);

  const styles = createStyle(screen, appInfo);

  if (loadingPage) return <Loading />;
  const backgroundImage =
    screen?.backgroundImage || screen.attributes?.backgroundImage;

  return (
    <View style={styles.container} nativeID="render-container">
      {backgroundImage && (
        <BackgroundImage
          source={backgroundImage}
          resizeMode={screen?.attributes?.backgroundSize}
          positionX={screen?.attributes?.backgroundPositionX}
          positionY={screen?.attributes?.backgroundPositionY}
        />
      )}

      {!isWeb && !isResponsive && (
        <StatusBar
          isShowStatusBar={isShowStatusBar}
          barStyle={screen?.attributes?.statusBarStyle}
        />
      )}

      <QrCodeScanner />

      <SafeAreaView
        style={styles.safeViewContainer}
        nativeID="render-safe-area-view"
      >
        {arrConstrain.length > 0 && (
          <ConstrainObject items={arrConstrain} screen={screen} />
        )}
        {arrNormal.length > 0 && (
          <NormalComponent arrNormal={arrNormal} screen={screen} />
        )}
      </SafeAreaView>

      {arrFixed.length > 0 && (
        <FixedComponent arrFixed={arrFixed} screen={screen} />
      )}
    </View>
  );
}

export default React.memo(
  Render,
  memoriesConditional(['dependencies', 'screen', 'Render'])
);
